import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import SiteChrome from './SiteChrome';

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [referralsPage, setReferralsPage] = useState({ items: [], page: 1, pageSize: 10, totalCount: 0, totalPages: 0 });
  const [isAffiliate, setIsAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralsLoading, setReferralsLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareStatus, setShareStatus] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showSellingHints, setShowSellingHints] = useState(false);

  const loadReferrals = async (page = 1) => {
    setReferralsLoading(true);
    try {
      const data = await api.getAffiliateReferrals(page, referralsPage.pageSize || 10);
      setReferralsPage(data || { items: [], page, pageSize: 10, totalCount: 0, totalPages: 0 });
    } finally {
      setReferralsLoading(false);
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [dashboardData, referralData] = await Promise.all([
          api.getAffiliateDashboard(),
          api.getAffiliateReferrals(1, 10)
        ]);
        setDashboard(dashboardData);
        setReferralsPage(referralData || { items: [], page: 1, pageSize: 10, totalCount: 0, totalPages: 0 });
        setIsAffiliate(true);
      } catch (err) {
        setIsAffiliate(false);
        setError('You are not registered as an affiliate. Please sign up for the affiliate program first.');
      } finally {
        setLoading(false);
        setReferralsLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const referralLink = dashboard?.referralCode
    ? `https://bettermedaily.co.za/download.html?referralCode=${encodeURIComponent(dashboard.referralCode)}`
    : '';

  const shareMessage = referralLink
    ? `Imagine a diary that talks back.
Better Me helps you reflect, spot your patterns, improve your habits, track spending, and plan your life better day by day.

Please use my referral code: ${dashboard.referralCode}

Download here:
${referralLink}`
    : '';

  const showShareStatus = (message) => {
    setShareStatus(message);
    window.setTimeout(() => setShareStatus(''), 2000);
  };

  const copyReferralLink = async () => {
    if (!referralLink) return;
    await navigator.clipboard.writeText(referralLink);
    showShareStatus('Referral link copied.');
  };

  const copyShareMessage = async () => {
    if (!shareMessage) return;
    await navigator.clipboard.writeText(shareMessage);
    showShareStatus('Share message copied.');
  };

  const shareReferral = async () => {
    if (!shareMessage) return;

    if (navigator.share) {
      await navigator.share({
        title: 'Try Better Me',
        text: shareMessage,
        url: referralLink
      });
      return;
    }

    await navigator.clipboard.writeText(shareMessage);
    showShareStatus('Sharing is unavailable here, so the message was copied.');
  };

  const cardClass = 'bg-white rounded-md shadow-sm border border-gray-100 px-3 py-4 sm:px-4 sm:py-6';
  const sectionTitleClass = 'text-base font-bold text-gray-900 mb-4';
  const fieldClass = 'w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all duration-200';
  const primaryButtonClass = 'px-4 py-2.5 bg-primary-600 text-white rounded-md text-sm leading-none font-semibold hover:bg-primary-700 transition-colors';
  const darkButtonClass = 'px-4 py-2.5 bg-slate-900 text-white rounded-md text-sm leading-none font-semibold hover:bg-slate-800 transition-colors';
  const secondaryButtonClass = 'px-4 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-md text-sm leading-none font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const getReferralStatusMeta = (status) => {
    switch (status) {
      case 'Claimed':
        return {
          label: 'Claimed',
          activity: 'Referral claimed',
          badgeClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        };
      case 'Counted':
        return {
          label: 'Counted',
          activity: 'Referral counted',
          badgeClass: 'bg-sky-50 text-sky-700 border border-sky-200'
        };
      default:
        return {
          label: 'Pending',
          activity: 'Pending referral',
          badgeClass: 'bg-amber-50 text-amber-700 border border-amber-200'
        };
    }
  };

  const getPayoutStatusMeta = (status) => {
    switch (status) {
      case 'processed':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'declined':
        return 'bg-rose-50 text-rose-700 border border-rose-200';
      default:
        return 'bg-amber-50 text-amber-700 border border-amber-200';
    }
  };

  return (
    <SiteChrome>
      <div className="max-w-7xl mx-auto px-2.5 sm:px-6 py-3 sm:py-6 bg-gray-50">
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-sm text-gray-600">Loading your affiliate dashboard...</p>
            </div>
          </div>
        )}

        {!loading && !isAffiliate && (
          <div className={`${cardClass} text-center`}>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Not an Affiliate Yet</h2>
            <p className="text-sm text-gray-600 mb-6">
              {error || 'You need to sign up for the affiliate program to access this dashboard.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className={primaryButtonClass}
            >
              Sign Up for Affiliate Program
            </button>
          </div>
        )}

        {!loading && isAffiliate && dashboard && (
          <div className="space-y-6 animate-fade-in">
            <div className="rounded-md bg-gradient-to-br from-sky-500 to-sky-600 text-white px-3 py-4 sm:px-4 sm:py-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
                <div>
                  <h1 className="text-xl font-bold mb-2">Welcome back, {dashboard.name || 'Influencer'}</h1>
                  <p className="text-sm text-white/90">Here is your affiliate performance overview</p>
                </div>
                <div className="sm:text-right">
                  <div className="text-xs text-white/80 mb-1">Total earned</div>
                  <div className="text-2xl font-bold">R{Number(dashboard.totalEarned || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-md p-2.5 sm:p-4 text-white min-w-0">
                  <div className="text-[10px] sm:text-xs opacity-90 mb-1.5 sm:mb-3 leading-tight">Available</div>
                  <div className="text-base sm:text-2xl font-bold mb-0.5 sm:mb-1 leading-none truncate">R{Number(dashboard.availableBalance || 0).toFixed(2)}</div>
                  <div className="text-[10px] sm:text-xs opacity-90 leading-tight">Ready to pay</div>
                </div>

                <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-md p-2.5 sm:p-4 text-white min-w-0">
                  <div className="text-[10px] sm:text-xs opacity-90 mb-1.5 sm:mb-3 leading-tight">Referrals</div>
                  <div className="text-base sm:text-2xl font-bold mb-0.5 sm:mb-1 leading-none truncate">{dashboard.totalReferrals || 0}</div>
                  <div className="text-[10px] sm:text-xs opacity-90 leading-tight">{dashboard.premiumReferrals || 0} premium</div>
                </div>

                <div className="bg-gradient-to-br from-violet-500 to-indigo-600 rounded-md p-2.5 sm:p-4 text-white min-w-0">
                  <div className="text-[10px] sm:text-xs opacity-90 mb-1.5 sm:mb-3 leading-tight">Conversion</div>
                  <div className="text-base sm:text-2xl font-bold mb-0.5 sm:mb-1 leading-none truncate">{Number(dashboard.conversionRate || 0).toFixed(2)}%</div>
                  <div className="text-[10px] sm:text-xs opacity-90 leading-tight">To premium</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-md p-3 sm:p-4">
                <h3 className={sectionTitleClass}>Recent Activity</h3>
                <div className="space-y-3">
                  {(dashboard.referrals || []).map((referral) => (
                    <div key={referral.id} className="flex items-center justify-between gap-3 p-3 bg-white rounded-md border border-gray-100">
                      <div>
                        {(() => {
                          const statusMeta = getReferralStatusMeta(referral.status);
                          return (
                            <>
                              <div className="text-sm font-medium text-gray-900">{statusMeta.activity}</div>
                              <div className="mt-1 flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold leading-none ${statusMeta.badgeClass}`}>
                                  {statusMeta.label}
                                </span>
                                <span className="text-sm text-gray-500">{referral.referredEmail}</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      <div className="text-sm text-green-600 font-medium whitespace-nowrap">+R{Number(referral.commission || 0).toFixed(2)}</div>
                    </div>
                  ))}
                  {(dashboard.referrals || []).length === 0 && (
                    <p className="text-sm text-gray-500">No referral activity yet.</p>
                  )}
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className={sectionTitleClass}>Referral Link</h3>
                  <p className="text-sm text-gray-600 -mt-2">
                    Generate a ready-to-share referral link and message.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsShareModalOpen(true)}
                  className={primaryButtonClass}
                >
                  Generate Referral Link
                </button>
              </div>

              {shareStatus && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3 mt-4">
                  <p className="text-green-800 text-sm">{shareStatus}</p>
                </div>
              )}

              <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 sm:p-4">
                <p className="text-sm text-gray-700 mb-1">Your referral code</p>
                <p className="text-lg font-semibold text-gray-900">{dashboard.referralCode || 'Not available yet'}</p>
              </div>

              <div className="mt-4 rounded-md border border-sky-200 bg-sky-50 p-3 sm:p-4">
                <h4 className="text-sm font-semibold text-sky-950 mb-1">What counts as a valid referral</h4>
                <p className="text-sm text-sky-900 leading-6">
                  A referral counts once the user verifies their email and completes onboarding.
                </p>
              </div>
            </div>

            <div className={cardClass}>
              <h3 className={sectionTitleClass}>Referral List</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-3 font-semibold text-gray-700 sm:hidden">Referral</th>
                      <th className="hidden sm:table-cell text-left py-3 px-3 font-semibold text-gray-700">Name</th>
                      <th className="hidden sm:table-cell text-left py-3 px-3 font-semibold text-gray-700">Email</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Date</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralsPage.items.map((referral) => (
                      <tr key={referral.id} className="border-b border-gray-100">
                        <td className="py-3 px-3 sm:hidden">
                          <div className="text-gray-900 font-medium">{referral.referredName || 'Unknown user'}</div>
                          <div className="text-gray-500 text-xs mt-0.5 break-all">{referral.referredEmail}</div>
                        </td>
                        <td className="hidden sm:table-cell py-3 px-3 text-gray-700">{referral.referredName || 'Unknown user'}</td>
                        <td className="hidden sm:table-cell py-3 px-3 text-gray-700 break-all">{referral.referredEmail}</td>
                        <td className="py-3 px-3">
                          {(() => {
                            const statusMeta = getReferralStatusMeta(referral.status);
                            return (
                              <span className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold leading-none ${statusMeta.badgeClass}`}>
                                {statusMeta.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-3 px-3 text-gray-700">{new Date(referral.createdAt).toLocaleDateString()}</td>
                        <td className="py-3 px-3 text-green-600 font-medium">R{Number(referral.commission || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {!referralsLoading && referralsPage.items.length === 0 && (
                      <tr>
                        <td colSpan="6" className="py-6 px-3 text-center text-gray-500">No referrals yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                <p className="text-sm text-gray-500">
                  {referralsPage.totalCount > 0
                    ? `Showing ${referralsPage.items.length} of ${referralsPage.totalCount} referrals`
                    : 'No referrals to show'}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => loadReferrals(referralsPage.page - 1)}
                    disabled={referralsLoading || referralsPage.page <= 1}
                    className={secondaryButtonClass}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600 min-w-[88px] text-center">
                    Page {referralsPage.totalPages === 0 ? 0 : referralsPage.page} of {referralsPage.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => loadReferrals(referralsPage.page + 1)}
                    disabled={referralsLoading || referralsPage.page >= referralsPage.totalPages}
                    className={secondaryButtonClass}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <h3 className={sectionTitleClass}>Payout History</h3>
              <div className="space-y-3 mb-6">
                {(dashboard.payoutRequests || []).map((payout) => (
                  <div key={payout.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-md border border-gray-100">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium text-gray-900">Payout</div>
                        <span className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold leading-none ${getPayoutStatusMeta(payout.status)}`}>
                          {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Requested: {new Date(payout.requestedAt).toLocaleDateString()}
                        {payout.processedAt ? ` | Processed: ${new Date(payout.processedAt).toLocaleDateString()}` : ''}
                      </div>
                    </div>
                    <div className="text-sm text-green-600 font-medium whitespace-nowrap">R{Number(payout.amount || 0).toFixed(2)}</div>
                  </div>
                ))}
                {(dashboard.payoutRequests || []).length === 0 && (
                  <p className="text-sm text-gray-500">No payout requests yet.</p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h3 className={sectionTitleClass}>Next Payout</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your current available balance is{' '}
                  <span className="font-medium text-green-600">R{Number(dashboard.availableBalance || 0).toFixed(2)}</span>.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Referrals count after email verification and onboarding are complete. Earnings become claimed only once a payout is processed.
                </p>
                <Link to="/payout" className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium">
                  Request Payout
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {isShareModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 px-3 py-4">
          <div className="w-full max-w-xl rounded-xl bg-white shadow-2xl border border-gray-100 p-4 sm:p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Share Your Referral Link</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Copy your direct download link or use the full message below.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsShareModalOpen(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            {shareStatus && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                <p className="text-green-800 text-sm">{shareStatus}</p>
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-2">Referral Link</label>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                type="text"
                value={referralLink}
                readOnly
                className={`flex-1 ${fieldClass}`}
              />
              <button
                type="button"
                onClick={copyReferralLink}
                className={primaryButtonClass}
              >
                Copy Link
              </button>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">Share Message</label>
            <textarea
              value={shareMessage}
              readOnly
              rows={6}
              className={`${fieldClass} text-gray-700 mb-4`}
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={shareReferral}
                className={darkButtonClass}
              >
                Share
              </button>
              <button
                type="button"
                onClick={() => setShowSellingHints((current) => !current)}
                className={secondaryButtonClass}
              >
                {showSellingHints ? 'Hide Selling Hints' : 'Get Selling Hints'}
              </button>
              <button
                type="button"
                onClick={copyShareMessage}
                className={primaryButtonClass}
              >
                Copy Message
              </button>
            </div>

            {showSellingHints && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
                <h4 className="text-sm font-bold text-amber-950 mb-3">Selling Hints</h4>
                <div className="space-y-3 text-sm text-amber-900 leading-6">
                  <p>Lead with use cases people already want: journaling, habits, spending awareness, and planning.</p>
                  <p>Use language like “spot your patterns”, “reflect daily”, or “a diary that talks back” rather than clinical or guaranteed-outcome claims.</p>
                  <p>Best-performing content is usually personal and practical: show how you review your day, reset your week, or notice spending triggers.</p>
                  <p>Do not describe Better Me as therapy, diagnosis, treatment, crisis support, or regulated financial advice.</p>
                </div>
                <div className="mt-4 rounded-lg bg-white border border-amber-100 p-3">
                  <p className="text-xs font-semibold text-amber-950 mb-2">Sample angle</p>
                  <p className="text-sm text-gray-700 leading-6">
                    Imagine a diary that talks back. Better Me helps you reflect, spot your patterns, improve your habits, track spending, and plan your life better day by day.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </SiteChrome>
  );
};

export default Dashboard;
